import React, { FC } from 'react';
import { Stack } from '@mui/material';
import Accordion, { AccordionItem } from '@ingka/accordion';
import useTranslation from '../../hooks/useTranslation';
import { FinancesByFyProps } from './FinancesByFyProps';
import FinancesBlock from './FinancesBlock';
import { calculateTotal } from './utils';
import Divider from '../Divider';
import { useFormContext } from 'react-hook-form';
import { getCurrencyRate } from '../ProjectTabFinancials/utils';

const FinancesByFy: FC<FinancesByFyProps> = ({
  handleFyItemChange,
  fields,
  investments,
  currency,
  currencies,
  countryCode,
}) => {
  const { getValues, watch } = useFormContext();
  const isep = getValues('isep');
  const disabledFields = watch('disabledFields');

  const t = useTranslation();
  return (
    <Stack>
      <Accordion>
        {fields.length > 0 &&
          fields.map((fy, index) => {
            const hasBaseline = !!investments?.[index]?.BASELINE;
            const showSplit = isep || hasBaseline;
            return (
              <AccordionItem id={fy?.id} key={fy?.id} title={fy?.fiscalYear}>
                <Stack spacing={2}>
                  <Stack spacing={2} direction={'row'}>
                    <FinancesBlock
                      nameApendix={`investments.${index}.BUDGET`}
                      currency={currency}
                      fullWidth={!showSplit}
                      disabled={isep || disabledFields?.includes('ALL')}
                      onChange={handleFyItemChange}
                      dataTestIdApendix={fy?.fiscalYear}
                      total={calculateTotal(investments?.[index]?.BUDGET)}
                      title={t('finances.projectBudget')}
                      disablePropertyInvestmentValidation={isep}
                      currencyRate={getCurrencyRate(
                        countryCode,
                        currencies,
                        fy?.fiscalYear,
                      )}
                    />
                    {showSplit && (
                      <FinancesBlock
                        nameApendix={`investments.${index}.FORECAST`}
                        currency={currency}
                        onChange={handleFyItemChange}
                        dataTestIdApendix={fy?.fiscalYear}
                        disabled={disabledFields?.includes('ALL')}
                        total={calculateTotal(investments?.[index]?.FORECAST)}
                        title={'Forecast'}
                        currencyRate={getCurrencyRate(
                          countryCode,
                          currencies,
                          fy?.fiscalYear,
                        )}
                      />
                    )}
                  </Stack>
                  <Divider />
                  {showSplit && (
                    <>
                      <Stack spacing={2} direction={'row'}>
                        {hasBaseline && (
                          <FinancesBlock
                            nameApendix={`investments.${index}.BASELINE`}
                            currency={currency}
                            disabled={true}
                            onChange={handleFyItemChange}
                            dataTestIdApendix={fy?.fiscalYear}
                            total={calculateTotal(
                              investments?.[index]?.BASELINE,
                            )}
                            title={'Baseline'}
                            currencyRate={getCurrencyRate(
                              countryCode,
                              currencies,
                              fy?.fiscalYear,
                            )}
                          />
                        )}
                        <FinancesBlock
                          nameApendix={`investments.${index}.RELEASED_APPROVED_BUDGET`}
                          currency={currency}
                          onChange={handleFyItemChange}
                          fullWidth={!hasBaseline}
                          disabled={disabledFields?.includes('approvedBudget')}
                          dataTestIdApendix={fy?.fiscalYear}
                          total={calculateTotal(
                            investments?.[index]?.RELEASED_APPROVED_BUDGET,
                          )}
                          title={'Released/Approved amount'}
                          currencyRate={getCurrencyRate(
                            countryCode,
                            currencies,
                            fy?.fiscalYear,
                          )}
                        />
                      </Stack>
                      <Divider />
                      {investments?.[index]?.ACTUALS && (
                        <>
                          <FinancesBlock
                            nameApendix={`investments.${index}.ACTUALS`}
                            currency={currency}
                            disabled={true}
                            onChange={handleFyItemChange}
                            fullWidth
                            dataTestIdApendix={fy?.fiscalYear}
                            total={calculateTotal(
                              investments?.[index]?.ACTUALS,
                            )}
                            title={'Actuals'}
                            disablePropertyInvestmentValidation
                            currencyRate={getCurrencyRate(
                              countryCode,
                              currencies,
                              fy?.fiscalYear,
                            )}
                          />
                          <Divider />
                        </>
                      )}

                      {investments?.[index]?.SUBMITTED_FORECAST && (
                        <>
                          <FinancesBlock
                            nameApendix={`investments.${index}.SUBMITTED_FORECAST`}
                            currency={currency}
                            onChange={handleFyItemChange}
                            disabled
                            fullWidth
                            dataTestIdApendix={fy?.fiscalYear}
                            total={calculateTotal(
                              investments?.[index]?.SUBMITTED_FORECAST,
                            )}
                            title={'P-Tools Forecast'}
                            disablePropertyInvestmentValidation
                            currencyRate={getCurrencyRate(
                              countryCode,
                              currencies,
                              fy?.fiscalYear,
                            )}
                          />
                          <Divider />
                        </>
                      )}
                    </>
                  )}
                </Stack>
              </AccordionItem>
            );
          })}
      </Accordion>
    </Stack>
  );
};

export default FinancesByFy;
